import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DepartmentDashboard_API } from "../../Constants/apiRoutes";
import axios from "axios";
import { Icon } from "@iconify/react";
import { Combobox } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { HiClipboardCheck } from "react-icons/hi";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";
import api from "../../Components/ProtectedRoute/axiosInstance"; 
import Datepicker from "react-tailwindcss-datepicker";
// Define the new icon components
const FinanceIcon = () => <Icon icon="mdi:finance" width={50} height={50} />;
const TechnicalIcon = () => <Icon icon="mdi:cog" width={50} height={50} />;
const SalesIcon = () => <Icon icon="mdi:chart-line" width={50} height={50} />;
const ProjectsIcon = () => (
  <Icon icon="mdi:clipboard-list-outline" width={50} height={50} />
);
const LegalIcon = () => <Icon icon="mdi:gavel" width={50} height={50} />;
// const ManagementIcon = () => <Icon icon="mdi:cog-outline" width={50} height={50} />;
const ManagementIcon = () => <Icon icon="mdi:account-tie" width={50} height={50} />;
const OthersIcon = () => (
  <Icon icon="mdi:dots-horizontal" width={50} height={50} />
);

// Define the icon mapping
const iconMapping = {
  1: <TechnicalIcon />, // Technical Function
  2: <FinanceIcon />, // Finance
  3: <Icon icon="mdi:account-group" width={50} height={50} />, // Human Resources
  4: <Icon icon="mdi:speaker" width={50} height={50} />, // Marketing
  5: <SalesIcon />, // Sales
  6: <ProjectsIcon />, // Projects
  7: <LegalIcon />, // Legal
  8: <ManagementIcon />, // Management
  9: <OthersIcon />, // Others
};

const functionsList = [
  { id: 1, name: "Technical", uuid: "4251-b2d7-b4c4a7aaf8f8" },
  { id: 2, name: "Finance", uuid: "4251-b2d7-b4c4a7bbf7f7" },
  { id: 3, name: "Human Resources", uuid: "4251-b2d7-b4c4a7ccf6f6" },
  { id: 4, name: "Marketing", uuid: "4251-b2d7-b4c4a7ddf5f5" },
  { id: 5, name: "Sales", uuid: "4251-b2d7-b4c4a7eef4f4" },
  { id: 6, name: "Projects", uuid: "4251-b2d7-b4c4a7fff3f3" },
  { id: 7, name: "Legal", uuid: "4251-b2d7-b4c4a7ggf2f2" },
  { id: 8, name: "Management", uuid: "4251-b2d7-b4c4a7ggf9f9" },
  { id: 9, name: "Others", uuid: "4251-b2d7-b4c4a7hhf1f1" },
];

function Department() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [departmentInfo, setDepartmentInfo] = useState([]);
  const [selectedBy, setSelectedBy] = useState(null); 
  const [isLoading, setIsLoading] = useState(false);
  const byOptions = [
    { id: "createdByMe", name: "Created By me" },
    { id: "pendingApprovals", name: "Pending Approvals" },
  ];
  
 // === Default Date: last 7 days with time ===
 const today = new Date();
 const sevenDaysAgo = new Date();
 sevenDaysAgo.setDate(today.getDate() - 7);
 
 const [value, setValue] = useState({
   startDate: sevenDaysAgo.toISOString(), 
   endDate: today.toISOString(),
 });
  useEffect(() => {
    const fetchDepartmentData = async () => {
      const token = localStorage.getItem("token");
      const departmentIDsString = localStorage.getItem("DepartmentIDs");
      const userId = localStorage.getItem("UserID");
      setIsLoading(true);
      try {
        const response = await api.get(DepartmentDashboard_API, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            filter: selectedBy,
            UserId: userId,
             StartDate: value.startDate,
        EndDate: value.endDate,
          },
        });

        const departmentData = response.data.departmentData;
        const departmentIDs = departmentIDsString
          ? departmentIDsString.split(",").map((id) => Number(id.trim()))
          : [];

        let matchedDepartments;

        if (selectedBy === "pendingApprovals") {
          // ✅ Show all departments from backend response directly
          matchedDepartments = departmentData.map((dept) => {
            const func = functionsList.find((f) => f.id === dept.DepartmentId);
            return {
              id: dept.DepartmentId,
              name: func?.name || "Unknown",
              uuid: func?.uuid || "",
              documentCount: dept.DocumentCount,
            };
          });
        } else {
          // 🔒 Filter based on user's accessible departments
          matchedDepartments = functionsList
            .filter((func) => departmentIDs.includes(func.id))
            .map((func) => {
              const match = departmentData.find(
                (dept) => Number(dept.DepartmentId) === func.id
              );
              return {
                id: func.id,
                name: func.name,
                uuid: func.uuid,
                documentCount: match ? match.DocumentCount : "0",
              };
            });
        }

        setDepartmentInfo(matchedDepartments);
      } catch (err) {
        console.error("Fetch Error:", err);
        setError("Failed to fetch data.");
      } finally {
          setIsLoading(false);
      }
    };

    fetchDepartmentData();
  }, [selectedBy,value.startDate, value.endDate]);

  const goToDepartment = (uuid) => {
    const url = `/documentsList?filter=${uuid}${
      selectedBy ? `&by=${selectedBy}` : ""
    }`;
    navigate(url);
  };

  const goToTechnical = () => goToDepartment("4251-b2d7-b4c4a7aaf8f8");
  const goToFinance = () => goToDepartment("4251-b2d7-b4c4a7bbf7f7");
  const goToHumanResource = () => goToDepartment("4251-b2d7-b4c4a7ccf6f6");
  const goToMarketing = () => goToDepartment("4251-b2d7-b4c4a7ddf5f5");
  const goToSales = () => goToDepartment("4251-b2d7-b4c4a7eef4f4");
  const goToProjects = () => goToDepartment("4251-b2d7-b4c4a7fff3f3");
  const goToLegal = () => goToDepartment("4251-b2d7-b4c4a7ggf2f2");
  const goToManagement = () => goToDepartment("4251-b2d7-b4c4a7ggf9f9");
  const goToOthers = () => goToDepartment("4251-b2d7-b4c4a7hhf1f1");
  // Check if there are no pending documents when filter is set to pendingApprovals
  const noPendingDocuments =
    selectedBy === "pendingApprovals" &&
    departmentInfo.length === 0 &&
    !isLoading &&
    !error;

  if (error) return <div>{error}</div>;

  return (
    <div className="main-container">
         {isLoading && <LoadingAnimation />}
          <div className="mx-auto p-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h2 className="heading">Functions</h2>
        <div className="flex flex-wrap items-center gap-4">
          {/* Created By / Approved By Dropdown */}
          <div className="w-64">
            <Combobox value={selectedBy} onChange={setSelectedBy}>
              <div className="relative">
                <Combobox.Input
                  className="w-full h-10 px-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  displayValue={(by) => {
                    const option = byOptions.find((opt) => opt.id === by);
                    return option
                      ? option.name
                      : "Created By/Pending Approvals";
                  }}
                  placeholder="Created By/Pending Approvals"
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </Combobox.Button>
                <Combobox.Options className="absolute z-30 w-full mt-1 bg-white rounded-md shadow-lg">
                  {byOptions.map((by) => (
                    <Combobox.Option
                      key={by.id}
                      value={by.id}
                      className={({ active }) =>
                        `p-2 cursor-pointer ${
                          active ? "bg-blue-500 text-white" : "text-gray-900"
                        }`
                      }
                    >
                      {by.name}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </div>
            </Combobox>
          </div>
          
              <div className="border-solid border-gray-400 border-[1px] rounded-lg w-64">
                      <Datepicker
                        popoverDirection="down"
                        showShortcuts={true}
                        showFooter={true}
                        placeholder="Start Date and End Date"
                        primaryColor={"purple"}
                        value={value}
                        onChange={(newValue) => setValue(newValue)}
                        className="w-full"
                      />
                    </div>
                  
        </div>
      </div>
</div>
      {noPendingDocuments ? (
        <div className="text-center py-16 px-6 max-w-lg mx-auto">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-amber-50 rounded-full">
              <HiClipboardCheck size={48} className="text-[#8B4513]" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-700">
            No pending documents for approval
          </div>
          <div className="mt-4 text-gray-500 text-lg">
            You don't have any documents waiting for your approval at this time.
          </div>
          <div className="mt-8">
            <button
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2 bg-[#8B4513] text-white rounded-md transition-colors font-medium"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-2 mb-8 mt-2">
          {departmentInfo.map((dept) => (
            <div
              key={dept.name}
              className="relative h-32 p-4 bg-white bg-opacity-90 shadow-lg shadow-[#8B4513]/90 rounded-2xl overflow-hidden"
              onClick={() => {
                if (dept.name === "Technical") goToTechnical();
                else if (dept.name === "Finance") goToFinance();
                else if (dept.name === "Human Resources") goToHumanResource();
                else if (dept.name === "Marketing") goToMarketing();
                else if (dept.name === "Sales") goToSales();
                else if (dept.name === "Projects") goToProjects();
                else if (dept.name === "Legal") goToLegal();
                else if (dept.name === "Management") goToManagement();
                else if (dept.name === "Others") goToOthers();
              }}
            >
              <div className="flex items-center justify-items-center p-6 py-3 w-full max-w-sm gap-4 text-[#8B4513]">
                {iconMapping[dept.id]}
                <div>
                  <h2 className="text-2xl font-bold text-[#8B4513]">
                    {dept.documentCount}
                  </h2>
                  <p className="text-lg text-[#8B4513] opacity-80 font-bold ml-1">
                    {dept.name}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Department;
